<template>
  <template v-if="!modDisabled">
    <vl-checkbox :key="key" :modelValue="data.isActive" :mod-disabled="data.changing" mod-switch @input="onTestChecked">
      <vl-loader v-if="data.changing" class="checkbox-loader" mod-small mod-message-hidden />
    </vl-checkbox>
  </template>
  <template v-else>
    <span>{{ data.isActive ? $t('general.yes') : $t('general.no') }}</span>
  </template>
</template>

<script lang="ts" setup>
export type DeliveryQualityCheckData = {
  test: TestDto;
  deliveryQualityCheckTypeId: number;
  isEnabled: boolean;
};

import { TestDto } from '@/api/backoffice/clients';
import { useModalStore } from '@/modules/core/store/modal.store';
import { computed, onMounted, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const modalStore = useModalStore();

const { t } = useI18n();

const props = defineProps<{
  modelValue: boolean;
  modDisabled?: boolean;
  test: TestDto;
  deliveryQualityCheckTypeId: number;
  useConfirmationDialog?: boolean;
  onCheckboxChange?: (deliveryQualityCheckData: DeliveryQualityCheckData) => Promise<boolean>;
}>();

const emit = defineEmits(['update:modelValue']);

const data = reactive({
  isActive: false,
  confirmed: false,
  changing: false,
  counter: 0
});

onMounted(() => {
  data.isActive = props.modelValue;
});

watch(
  () => props.modelValue,
  (val) => (data.isActive = val)
);

watch(
  () => data.isActive,
  () => emitChanged(),
  { deep: true }
);

const key = computed(() => {
  return `gecko-test-checkbox-${props.test.code}-${data.counter}`;
});

const emitChanged = () => {
  emit('update:modelValue', data.isActive);
};

const onTestChecked = async () => {
  const isActive = !data.isActive;
  if (!isActive && props.useConfirmationDialog) {
    showTestCheckedConfirmDialog(isActive);
  } else {
    await updateTest(isActive);
  }
};

const showTestCheckedConfirmDialog = (isActive: boolean) => {
  const title = t('gecko-test-checkbox.confirmDialog.title').toString();
  const okLabel = t('gecko-test-checkbox.confirmDialog.okLabel').toString();
  const confirmationMessage = t('gecko-test-checkbox.confirmDialog.confirmationMessage', {
    code: props.test.code
  }).toString();
  const confirm = async () => await onTestCheckedConfirmDialogConfirmed(isActive);
  const cancel = async () => await onTestCheckedConfirmDialogCanceled(isActive);
  modalStore.showConfirmModal({ title, props: { confirmationMessage, okLabel, confirm, cancel } });
};

const updateTest = async (isActive: boolean) => {
  const success = await handleOnCheckboxChangedAsync(isActive);
  if (success) {
    data.isActive = isActive;
  } else {
    resetAndForceRerender(!isActive);
  }
};

const onTestCheckedConfirmDialogConfirmed = async (isActive: boolean) => {
  const success = await handleOnCheckboxChangedAsync(isActive);
  if (success) {
    data.confirmed = true;
    data.isActive = false;
  } else {
    resetAndForceRerender(true);
  }
  return success;
};

const onTestCheckedConfirmDialogCanceled = async (isActive: boolean) => {
  if (!data.confirmed && !isActive) {
    resetAndForceRerender(true);
  }
  data.confirmed = false;
};

const handleOnCheckboxChangedAsync = async (isEnabled: boolean) => {
  data.changing = true;
  try {
    const deliveryQualityCheckData: DeliveryQualityCheckData = {
      deliveryQualityCheckTypeId: props.deliveryQualityCheckTypeId,
      test: props.test,
      isEnabled
    };
    return await props.onCheckboxChange?.(deliveryQualityCheckData);
  } finally {
    data.changing = false;
  }
};

const resetAndForceRerender = (isActive: boolean) => {
  data.isActive = isActive;
  data.counter++; // force re-render of checkbox because data.isActive did not change
};
</script>

<style scoped>
.checkbox-loader .vl-loader {
  width: 2rem;
  height: 1rem;
}
</style>
